import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Badge,
  Box,
  Button,
  Divider,
  Flex,
  Heading,
  HStack,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  SimpleGrid,
  Spinner,
  Stack,
  Text,
  useToast,
  VStack,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
} from "@chakra-ui/react";
import { SearchIcon, ChevronDownIcon } from "@chakra-ui/icons";
import { RefreshCcw } from "lucide-react";

import StatusFilterChips from "./StatusFilterChips";
import BookingDetailsDrawer from "./BookingDetailsDrawer";
import BookingStatusTimeline from "./CarBookingStatusTimeline";
import { useAuth } from "../../../auth/AuthContext";

import {
  getAllowedStatusesByRole,
  prettyStatus,
  statusColor,
  type BookingStatus,
} from "./hireStatusRules";
import { useUserRole } from "../../../store/hooks/useUserRole";
import { useCustomTheme } from "../../../contexts/CustomThemeContext";
import GlobalSpinner from "../../CustomReusables/GlobalSpinner";

const cardsBorderRadii ="8px";

const CARS_API =
  (process.env.REACT_APP_BACKEND_URL || "") + "/api/v1/travel/cars";

type Status =
  | "all"
  | "pending"
  | "awaiting_owner_approval"
  | "approved"
  | "declined"
  | "paid"
  | "active"
  | "completed"
  | "cancelled"
  | "refunded";

function money(n: number) {
  return `MWK ${Number(n || 0).toLocaleString()}`;
}

function safeStr(v: any) {
  return String(v ?? "").trim();
}

export default function OwnerHireDashboard() {
  const toast = useToast();
  const { user } = useAuth();
  const { role, loadingRole } = useUserRole();
  const { theme } = useCustomTheme();

  const ownerId = user?.uid || "";

  const [loading, setLoading] = useState(false);
  const [actingId, setActingId] = useState<string | null>(null);

  const [earnings, setEarnings] = useState<{ total: number; count: number }>({
    total: 0,
    count: 0,
  });
  const [bookings, setBookings] = useState<any[]>([]);
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<Status>("all");

  const [selected, setSelected] = useState<any | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const load = async () => {
    if (!ownerId) {
      toast({ title: "Owner ID missing", status: "warning" });
      return;
    }

    setLoading(true);
    try {
      const [eRes, bRes] = await Promise.all([
        axios.get(`${CARS_API}/hire/owner/earnings`, { params: { ownerId } }),
        axios.get(`${CARS_API}/hire/owner/bookings`, { params: { ownerId } }),
      ]);

      if (eRes.data?.ok)
        setEarnings(eRes.data.earnings || { total: 0, count: 0 });
      if (bRes.data?.ok) setBookings(bRes.data.bookings || []);
    } catch (err: any) {
      toast({
        title: "Failed to load owner data",
        description:
          err?.response?.data?.error || err?.message || "Please try again",
        status: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const decide = async (bookingId: string, decision: "approve" | "decline") => {
    try {
      setActingId(bookingId);

      const res = await axios.post(
        `${CARS_API}/hire/bookings/${bookingId}/owner-decision`,
        {
          ownerId,
          decision,
          notes: "",
        },
      );

      if (!res.data?.ok) throw new Error(res.data?.error || "Failed");

      toast({
        title: decision === "approve" ? "Booking approved" : "Booking declined",
        status: "success",
      });

      const updated = res.data.booking;
      setSelected((prev: any) =>
        prev && prev._id === bookingId ? updated : prev,
      );

      await load();
    } catch (err: any) {
      toast({
        title: "Decision failed",
        description:
          err?.response?.data?.error || err?.message || "Please try again",
        status: "error",
      });
    } finally {
      setActingId(null);
    }
  };

  const updateBookingStatus = async (
    bookingId: string,
    status: BookingStatus,
  ) => {
    try {
      setActingId(bookingId);

      const res = await axios.patch(
        `${CARS_API}/hire/bookings/${bookingId}/status`,
        {
          actorId: ownerId,
          actorRole: role,
          status,
          notes: "",
        },
      );

      if (!res.data?.ok) throw new Error(res.data?.error || "Failed");

      toast({
        title: "Status updated",
        description: `Updated to ${prettyStatus(status)}`,
        status: "success",
      });

      const updated = res.data.booking;
      setSelected((prev: any) =>
        prev && prev._id === bookingId ? updated : prev,
      );

      await load();
    } catch (err: any) {
      toast({
        title: "Status update failed",
        description:
          err?.response?.data?.error || err?.message || "Please try again",
        status: "error",
      });
    } finally {
      setActingId(null);
    }
  };

  const pendingReviewCount = useMemo(
    () => bookings.filter((b) => b.status === "awaiting_owner_approval").length,
    [bookings],
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return bookings.filter((b) => {
      const matchesStatus =
        statusFilter === "all" ? true : b.status === statusFilter;

      if (!q) return matchesStatus;

      const id = safeStr(b._id).toLowerCase();
      const status = safeStr(b.status).toLowerCase();
      const phone = safeStr(b.phoneNumber).toLowerCase();
      const carId = safeStr(b.carId).toLowerCase();
      const pickup = b.pickupAt
        ? new Date(b.pickupAt).toLocaleString().toLowerCase()
        : "";
      const ret = b.returnAt
        ? new Date(b.returnAt).toLocaleString().toLowerCase()
        : "";

      const matchesQuery =
        id.includes(q) ||
        status.includes(q) ||
        phone.includes(q) ||
        carId.includes(q) ||
        pickup.includes(q) ||
        ret.includes(q);

      return matchesStatus && matchesQuery;
    });
  }, [bookings, query, statusFilter]);

  const openDetails = (b: any) => {
    setSelected(b);
    setDrawerOpen(true);
  };

  return (
    <Box
      p={{ base: 4, md: 8 }}
      bg={theme.surfaceAlt}
      minH="100%"
      color={theme.text}
    >
      <Flex
        justify="space-between"
        align={{ base: "start", md: "center" }}
        gap={4}
        flexWrap="wrap"
      >
        <Box>
          <Heading size="lg" color={theme.text}>
            Owner Hire Dashboard
          </Heading>
          <Text color={theme.textMuted} mt={1}>
            Review bookings, update statuses, and monitor earnings.
          </Text>
        </Box>

        <HStack>
          <IconButton
            aria-label="Refresh"
            icon={<RefreshCcw size={18} />}
            onClick={load}
            variant="outline"
            bg={theme.surface}
            borderColor={theme.borderStrong}
            color={theme.text}
            _hover={{ bg: theme.hover }}
          />
        </HStack>
      </Flex>

      <SimpleGrid columns={{ base: 1, sm: 2, lg: 4 }} spacing={4} mt={6}>
        <Box
          p={4}
          border="1px solid"
          borderColor={theme.border}
          borderRadius={cardsBorderRadii}
          bg={theme.surface}
          boxShadow={theme.shadowSm}
        >
          <Text color={theme.textSubtle} fontSize="xs">
            Total Earnings
          </Text>
          <Text fontSize="2xl" fontWeight="bold" color={theme.text}>
            {money(earnings.total)}
          </Text>
          <Text fontSize="xs" color={theme.textSubtle} mt={1}>
            Paid / active / completed
          </Text>
        </Box>

        <Box
          p={4}
          border="1px solid"
          borderColor={theme.border}
          borderRadius={cardsBorderRadii}
          bg={theme.surface}
          boxShadow={theme.shadowSm}
        >
          <Text color={theme.textSubtle} fontSize="xs">
            Counted Bookings
          </Text>
          <Text fontSize="2xl" fontWeight="bold" color={theme.text}>
            {earnings.count}
          </Text>
          <Text fontSize="xs" color={theme.textSubtle} mt={1}>
            Included in earnings
          </Text>
        </Box>

        <Box
          p={4}
          border="1px solid"
          borderColor="yellow.200"
          borderRadius={cardsBorderRadii}
          bg={theme.surface}
          boxShadow={theme.shadowSm}
        >
          <Text color={theme.textSubtle} fontSize="xs">
            Pending Review
          </Text>
          <Text fontSize="2xl" fontWeight="bold" color="yellow.600">
            {pendingReviewCount}
          </Text>
          <Text fontSize="xs" color={theme.textSubtle} mt={1}>
            Awaiting approval / decline
          </Text>
        </Box>

        <Box
          p={4}
          border="1px solid"
          borderColor={theme.border}
          borderRadius={cardsBorderRadii}
          bg={theme.surface}
          boxShadow={theme.shadowSm}
        >
          <Text color={theme.textSubtle} fontSize="xs">
            Visible Results
          </Text>
          <Text fontSize="2xl" fontWeight="bold" color={theme.text}>
            {filtered.length}
          </Text>
          <Text fontSize="xs" color={theme.textSubtle} mt={1}>
            After search & filters
          </Text>
        </Box>
      </SimpleGrid>

      <Stack
        direction={{ base: "column", md: "row" }}
        spacing={4}
        mt={6}
        align={{ md: "center" }}
      >
        <InputGroup maxW={{ base: "100%", md: "420px" }}>
          <InputLeftElement pointerEvents="none">
            <SearchIcon color={theme.textSubtle} />
          </InputLeftElement>
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by booking ID, phone, status, carId, dates..."
            bg={theme.inputBg}
            color={theme.text}
            borderColor={theme.inputBorder}
            _placeholder={{ color: theme.inputPlaceholder }}
            _hover={{ borderColor: theme.inputBorderFocus }}
            _focus={{
              borderColor: theme.inputBorderFocus,
              boxShadow: `0 0 0 1px ${theme.inputBorderFocus}`,
            }}
          />
        </InputGroup>

        <StatusFilterChips value={statusFilter} onChange={setStatusFilter} />

        {(loading || loadingRole) && (
          <HStack
            bg={theme.surface}
            px={3}
            borderRadius={cardsBorderRadii}
            border="1px solid"
            borderColor={theme.border}
          >
            <GlobalSpinner />
            <Text color={theme.textMuted} fontSize="sm">
              {loadingRole ? "Loading role..." : "Loading…"}
            </Text>
          </HStack>
        )}
      </Stack>

      <Divider my={6} borderColor={theme.divider} />

      <VStack align="stretch" spacing={4}>
        {filtered.length === 0 ? (
          <Box
            p={8}
            border="1px dashed"
            borderColor={theme.borderStrong}
            borderRadius={cardsBorderRadii}
            textAlign="center"
            bg={theme.surface}
          >
            <Text fontWeight="semibold" color={theme.text}>
              No bookings found
            </Text>
            <Text color={theme.textMuted} fontSize="sm" mt={1}>
              Try changing filters or searching by status/ID.
            </Text>
          </Box>
        ) : (
          filtered.map((b: any) => {
            const totalDue = Number(b?.price?.totalDue || 0);
            const awaiting = b.status === "awaiting_owner_approval";
            const isActing = actingId === b._id;
            const allowedStatuses = getAllowedStatusesByRole(
              b.status as BookingStatus,
              role,
            );

            return (
              <Box
                key={b._id}
                p={4}
                border="1px solid"
                borderColor={awaiting ? "yellow.200" : theme.border}
                borderRadius={cardsBorderRadii}
                bg={theme.surface}
                cursor="pointer"
                boxShadow={theme.shadowSm}
                _hover={{
                  borderColor: awaiting ? "yellow.300" : theme.borderStrong,
                  boxShadow: theme.shadowMd,
                }}
                transition="all 0.2s ease"
                onClick={() => openDetails(b)}
              >
                <Stack
                  direction={{ base: "column", xl: "row" }}
                  spacing={4}
                  justify="space-between"
                  align={{ xl: "center" }}
                >
                  <Box flex="1">
                    <HStack spacing={3} flexWrap="wrap" align="center">
                      <Text fontWeight="bold" fontSize="md" color={theme.text}>
                        Booking #{String(b._id).slice(-6)}
                      </Text>

                      <Badge
                        colorScheme={statusColor(b.status)}
                        px={2.5}
                        py={1}
                        borderRadius={cardsBorderRadii}
                        textTransform="none"
                      >
                        {prettyStatus(b.status)}
                      </Badge>

                      {awaiting && (
                        <Badge
                          colorScheme="yellow"
                          variant="subtle"
                          borderRadius={cardsBorderRadii}
                          px={2}
                        >
                          Action needed
                        </Badge>
                      )}
                    </HStack>

                    <Text fontSize="sm" color={theme.textMuted} mt={2}>
                      {new Date(b.pickupAt).toLocaleString()} →{" "}
                      {new Date(b.returnAt).toLocaleString()}
                    </Text>

                    <Text fontSize="sm" color={theme.textMuted} mt={1}>
                      Phone: {b.phoneNumber || "—"}
                      {b.insurancePlan && b.insurancePlan !== "none" && (
                        <> • Insurance: {b.insurancePlan}</>
                      )}
                      {b.deliveryRequested && <> • Delivery: Yes</>}
                    </Text>

                    <SimpleGrid columns={{ base: 1, sm: 2 }} spacing={3} mt={3}>
                      <Box
                        p={3}
                        bg={theme.surfaceAlt}
                        borderRadius={cardsBorderRadii}
                        border="1px solid"
                        borderColor={theme.border}
                      >
                        <Text fontSize="xs" color={theme.textSubtle}>
                          Total Due
                        </Text>
                        <Text fontWeight="bold" color={theme.text}>
                          {money(totalDue)}
                        </Text>
                      </Box>
                    </SimpleGrid>
                  </Box>

                  <Box minW={{ base: "100%", xl: "420px" }}>
                    <BookingStatusTimeline status={b.status} compact />

                    <Flex
                      mt={4}
                      justify={{ base: "flex-start", xl: "flex-end" }}
                      gap={3}
                      wrap="wrap"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Button
                        variant="outline"
                        onClick={() => openDetails(b)}
                        borderColor={theme.borderStrong}
                        color={theme.text}
                        _hover={{ bg: theme.hover }}
                      >
                        View Details
                      </Button>

                      <Menu>
                        <MenuButton
                          as={Button}
                          rightIcon={<ChevronDownIcon />}
                          variant="outline"
                          isDisabled={
                            loadingRole ||
                            allowedStatuses.length === 0 ||
                            isActing
                          }
                          borderColor={theme.borderStrong}
                          color={theme.text}
                          _hover={{ bg: theme.hover }}
                        >
                          Update Status
                        </MenuButton>
                        <MenuList
                          bg={theme.surface}
                          borderColor={theme.border}
                          boxShadow={theme.shadowMd}
                        >
                          {allowedStatuses.length === 0 ? (
                            <MenuItem isDisabled>No allowed updates</MenuItem>
                          ) : (
                            allowedStatuses.map((s) => (
                              <MenuItem
                                key={s}
                                onClick={() => updateBookingStatus(b._id, s)}
                                _hover={{ bg: theme.hover }}
                              >
                                {prettyStatus(s)}
                              </MenuItem>
                            ))
                          )}
                        </MenuList>
                      </Menu>

                      {awaiting && (
                        <>
                          <Button
                            colorScheme="green"
                            onClick={() => decide(b._id, "approve")}
                            isLoading={isActing}
                            loadingText="Approving"
                          >
                            Approve
                          </Button>
                          <Button
                            colorScheme="red"
                            variant="outline"
                            onClick={() => decide(b._id, "decline")}
                            isLoading={isActing}
                            loadingText="Declining"
                          >
                            Decline
                          </Button>
                        </>
                      )}
                    </Flex>
                  </Box>
                </Stack>
              </Box>
            );
          })
        )}
      </VStack>

      <BookingDetailsDrawer
        isOpen={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        booking={selected}
        mode="owner"
        userRole={role}
        onApprove={() => selected && decide(selected._id, "approve")}
        onDecline={() => selected && decide(selected._id, "decline")}
        onUpdateStatus={(status: BookingStatus) =>
          selected && updateBookingStatus(selected._id, status)
        }
      />
    </Box>
  );
}
